import * as React from 'react';
import * as Features from '../../../../shared/Features';
import * as PhotoStatus from '../../../../shared/PhotoStatus';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuItem from '@mui/material/MenuItem';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LinkIcon from '@mui/icons-material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import { Translate } from '../../../../shared/LanguageService';
import Paper from '@mui/material/Paper';
import { useSelector } from 'react-redux';
import { getSiteName } from '../../../../shared/Site';
import * as Site from '../../../../shared/Site';
import { Resolution } from './Entities';

export type ImageDeliveryOptionsLabels = {
    downloadHighRes: string;
    downloadNormal: string;
    downloadLinkNormal: string;
    downloadLinkHigh: string;
    emailNormal: string;
    buttonLabel: string;
};
export type DeliveryOptionsProps = {
    emailImages(resolution: Resolution): void;
    downloadLink(resolution: Resolution): void;
    downloadImages(isNormal: boolean): void;
    labels: ImageDeliveryOptionsLabels;
    photoStatus: number;
    user: string;
    normalImagesAvailable: boolean;
    downloading: boolean;
    site: string;
};

export const ImageDeliveryOptionsDropdown: React.FC<DeliveryOptionsProps> = ({
    emailImages,
    downloadLink,
    downloadImages,
    labels,
    photoStatus,
    user,
    normalImagesAvailable,
    downloading,
    site
}) => {
    const numberOfImages = useSelector((state: any) =>
        state.imageProcessing.numberOfImages ? state.imageProcessing.numberOfImages.edited : 0
    );

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [isNormal, setIsNormal] = React.useState<boolean | null>();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadImages = (isNormal) => {
        downloadImages(isNormal);
        setIsNormal(isNormal);
    };

    const StyledMenuItem = ({ onClick, text, icon }) => {
        return (
            <MenuItem onClick={onClick}>
                <ListItemText
                    primaryTypographyProps={{
                        fontSize: '1.5rem'
                    }}
                >
                    {text}
                </ListItemText>
                <ListItemIcon sx={{ fontSize: 'large', paddingLeft: '1rem' }}>{icon}</ListItemIcon>
            </MenuItem>
        );
    };

    const handleDownloadLink = (resolution: Resolution) => {
        downloadLink(resolution);
        handleClose();
    };

    const DropdownMenu = () => {
        const canDownloadEdited =
            Features.hasFeature(Features.DownloadEditedImages, user) &&
            [
                PhotoStatus.CustomerApproved,
                PhotoStatus.Delivered,
                PhotoStatus.DeliveryError,
                PhotoStatus.ScheduledForCustomerApproval,
                PhotoStatus.DeliveryStarted
            ].includes(photoStatus);

        const canDownload =
            (canDownloadEdited || Features.hasFeature(Features.DownloadImages, user)) &&
            numberOfImages > 0;

        return (
            <Paper>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                    sx={{ borderRadius: 0 }}
                >
                    {canDownload && (
                        <Box>
                            <StyledMenuItem
                                onClick={() => handleDownloadImages(false)}
                                text={labels.downloadHighRes}
                                icon={
                                    downloading && isNormal === false ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <CloudDownloadIcon fontSize="large" />
                                    )
                                }
                            />
                            {normalImagesAvailable && <Divider />}
                            {normalImagesAvailable && (
                                <StyledMenuItem
                                    onClick={() => handleDownloadImages(true)}
                                    text={labels.downloadNormal}
                                    icon={
                                        downloading && isNormal === true ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <CloudDownloadIcon fontSize="large" />
                                        )
                                    }
                                />
                            )}
                            <Divider />
                        </Box>
                    )}

                    {canDownload && (
                        <Box>
                            <StyledMenuItem
                                onClick={() => {
                                    handleDownloadLink(Resolution.High);
                                }}
                                text={labels.downloadLinkHigh}
                                icon={<LinkIcon fontSize="large" />}
                            />
                            {normalImagesAvailable && <Divider />}
                            {normalImagesAvailable && (
                                <StyledMenuItem
                                    onClick={() => {
                                        handleDownloadLink(Resolution.Normal);
                                    }}
                                    text={labels.downloadLinkNormal}
                                    icon={<LinkIcon fontSize="large" />}
                                />
                            )}
                            <Divider />
                        </Box>
                    )}

                    {site !== getSiteName(Site.Zentuvo) && normalImagesAvailable ? (
                        <Box>
                            <StyledMenuItem
                                onClick={() => {
                                    emailImages(Resolution.Normal);
                                    handleClose();
                                }}
                                text={labels.emailNormal}
                                icon={<MailOutlineIcon fontSize="large" />}
                            />
                        </Box>
                    ) : null}
                </Menu>
            </Paper>
        );
    };

    return (
        <>
            <Button
                type="button"
                variant="contained"
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{
                    borderRadius: 0,
                    height: '38px',
                    maxWidth: '100%',
                    minWidth: '320px',
                    fontSize: '1.4rem',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                }}
                onClick={handleClick}
                endIcon={!open ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
            >
                {labels.buttonLabel}
            </Button>
            <DropdownMenu />
        </>
    );
};

function TranslatedImageDeliveryDropdown(props: Omit<DeliveryOptionsProps, 'labels'>) {
    let labels: ImageDeliveryOptionsLabels = {
        buttonLabel: Translate('image-processing/edited/button-label/download-or-send-images'),
        downloadHighRes: Translate('image-processing/edited/button-label/download-high-images'),
        downloadLinkNormal: Translate('image-processing/deliver/zip-download-link-normal'),
        downloadLinkHigh: Translate('image-processing/deliver/zip-download-link-high'),
        downloadNormal: Translate('image-processing/edited/button-label/download-normal-images'),

        emailNormal: Translate(
            'image-processing/edited/button-label/send-normal-pictures-as-attachment'
        )
    };

    return <ImageDeliveryOptionsDropdown {...props} labels={labels} />;
}

export default TranslatedImageDeliveryDropdown;
